import * as React from 'react';

import { Col, Form, Input, Radio, Rate, Row, Space } from 'antd';
import { useAppDispatch, useAppSelector } from 'src/types/reduxHelpers';

import { InterviewComponent } from 'src/components/interviewComponents/types/interviewComponent';
import { nameof } from 'ts-simple-nameof';
import { setGeneralFeedback } from 'src/store/modules/interviewModule';
import { useTranslation } from 'react-i18next';

interface GeneralFeedbackProps {

}

interface GeneralFeedbackFormValues {
    isEmployeeMoralePriority: boolean;
    isMentalPhysicHealthPriority: boolean;
    isExpectationMet: boolean;
    newJobAttractivness: string;
    newJobDecidingFactor: string;
    suggestToManagement: string;
    overallRating: number;
}

export const GeneralFeedback: InterviewComponent<GeneralFeedbackProps> = React.forwardRef((props, ref) => {
    const { t } = useTranslation();

    const dispatch = useAppDispatch();

    const [form] = Form.useForm<GeneralFeedbackFormValues>();

    const generalFeedback = useAppSelector((state) => state.interview.generalFeedback);

    React.useImperativeHandle(ref, () => ({
        submit: async () => {
            try {
                await form.validateFields();

                form.submit();

                return true;
            } catch (err) {
                return false;
            }
        },
    }));

    return (
        <Row
            align="middle"
            justify="center"
            gutter={[30, 20]}
        >
            <Col
                span={24}
            >
                <Form
                    layout="vertical"
                    form={form}
                    onFinish={(values) => {
                        console.table(values);

                        // We're storing 'agree'/'disagree'/'not applicable' values as:
                        // agree = true
                        // disagree = false
                        // not applicable = null
                        // but the form values have issues when defaulting to null.

                        // Lets make the 'not applicable' values 'na' and convert back and forth as needed
                        dispatch(setGeneralFeedback({
                            isEmployeeMoralePriority: values.isEmployeeMoralePriority,
                            isMentalPhysicHealthPriority: values.isMentalPhysicHealthPriority,
                            isExpectationMet: values.isExpectationMet,
                            newJobAttractivness: values.newJobAttractivness,
                            newJobDecidingFactor: values.newJobDecidingFactor,
                            suggestToManagement: values.suggestToManagement,
                            overallRating: values.overallRating,
                        }));
                    }}
                >
                    {/* Question 23 */}
                    <Form.Item
                        label={t('Components.InterviewComponents.GeneralFeedback.is_employee_morale_priority_label')}
                        name={nameof<GeneralFeedbackFormValues>(x => x.isEmployeeMoralePriority)}
                        rules={[{ required: true }]}
                        // Tan : refered back to jobDetail.tsx //
                        initialValue={generalFeedback?.isEmployeeMoralePriority}
                    >
                        <Radio.Group>
                            <Space direction="vertical">
                                <Radio value={true}>
                                    {t('Common.agree')}
                                </Radio>
                                <Radio value={false}>
                                    {t('Common.disagree')}
                                </Radio>
                            </Space>
                        </Radio.Group>
                    </Form.Item>

                    {/* Question 24 */}
                    <Form.Item
                        label={t('Components.InterviewComponents.GeneralFeedback.is_mental_physic_health_priority_label')}
                        name={nameof<GeneralFeedbackFormValues>(x => x.isMentalPhysicHealthPriority)}
                        rules={[{ required: true }]}
                        // Tan : refered back to jobDetail.tsx //
                        initialValue={generalFeedback?.isMentalPhysicHealthPriority}
                    >
                        <Radio.Group>
                            <Space direction="vertical">
                                <Radio value={true}>
                                    {t('Common.agree')}
                                </Radio>
                                <Radio value={false}>
                                    {t('Common.disagree')}
                                </Radio>
                            </Space>
                        </Radio.Group>
                    </Form.Item>

                    {/* Question 25 */}
                    <Form.Item
                        label={t('Components.InterviewComponents.GeneralFeedback.is_expectation_met_label')}
                        name={nameof<GeneralFeedbackFormValues>(x => x.isExpectationMet)}
                        rules={[{ required: true }]}
                        // Tan : refered back to jobDetail.tsx //
                        initialValue={generalFeedback?.isExpectationMet}
                    >
                        <Radio.Group>
                            <Space direction="vertical">
                                <Radio value={true}>
                                    {t('Common.agree')}
                                </Radio>
                                <Radio value={false}>
                                    {t('Common.disagree')}
                                </Radio>
                            </Space>
                        </Radio.Group>
                    </Form.Item>

                    {/* Question 26 */}
                    <Form.Item
                        label={t('Components.InterviewComponents.GeneralFeedback.new_job_attractivness_label')}
                        name={nameof<GeneralFeedbackFormValues>(x => x.newJobAttractivness)}
                        rules={[{ required: true }, { max: 280 }]}
                        initialValue={generalFeedback?.newJobAttractivness}
                    >
                        <Input placeholder={t('Common.answer_placeholder_text')} />
                    </Form.Item>

                    {/* Question 27 */}
                    <Form.Item
                        label={t('Components.InterviewComponents.GeneralFeedback.new_job_deciding_factor_label')}
                        name={nameof<GeneralFeedbackFormValues>(x => x.newJobDecidingFactor)}
                        initialValue={generalFeedback?.newJobDecidingFactor}
                        rules={[{ required: true }, { max: 280 }]}
                    >
                        <Input placeholder={t('Common.answer_placeholder_text')} />
                    </Form.Item>

                    {/* Question 28 */}
                    <Form.Item
                        label={t('Components.InterviewComponents.GeneralFeedback.suggest_to_management_label')}
                        name={nameof<GeneralFeedbackFormValues>(x => x.suggestToManagement)}
                        rules={[{ required: true }, { max: 280 }]}
                        initialValue={generalFeedback?.suggestToManagement}
                    >
                        <Input placeholder={t('Common.answer_placeholder_text')} />
                    </Form.Item>

                    {/* Question 29 */}
                    <Form.Item
                        label={t('Components.InterviewComponents.GeneralFeedback.overall_rating_label')}
                        initialValue={generalFeedback?.overallRating}
                        name={nameof<GeneralFeedbackFormValues>(x => x.overallRating)}
                        rules={[{ required: true }]}
                    >
                        <Rate />
                    </Form.Item>
                </Form>
            </Col>
        </Row>
    );
});

GeneralFeedback.displayName = 'GeneralFeedback';